import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CardHeader,
  Grid,
  Grid2,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { historiqueService } from "../service/HistoriqueService";
import { ArrowBack, SearchOutlined } from "@material-ui/icons";
import useToast from "../hooks/useToast";
import { TYPE_NOTIFICATION } from "./utils/NotificationBull";
import { LoadingButton } from "@mui/lab";
import { frFR } from "@mui/x-data-grid/locales";
import Menu from "./Menu";
import logo from "./logo.png";
import { useQuery } from "../hooks/useQuery";
import { useNavigate } from "react-router-dom";
import ImportExportIcon from "@material-ui/icons/ImportExport";
import { FileDownloadSharp } from "@mui/icons-material";
import { exportToExcel } from "../helpers/ExportHelpers";
import { isTokenCreatedToday } from "../helpers/TokenHelpers";
import StandardSkeletoon from "./reusable/loader/StandardSkeletoon";
import { formatPhoneNumber, showMoneyFormat } from "../helpers/ExtraHelpers";

export const EXTERNAL_POSTULANT_ATTRIBUTES_COLUMNS = [
  { field: "id_identite", headerName: "Id", width: 100 },
  { field: "full_name", headerName: "Nom et prénoms", width: 300 },
  { field: "reference", headerName: "Reference", width: 200 },
  { field: "adresse", headerName: "Adresse", width: 200 },
  { field: "district", headerName: "District", width: 200 },
  { field: "cin", headerName: "CIN", width: 200 },
  {
    field: "contact",
    headerName: "Contact",
    width: 200,
    valueFormatter: (params) => formatPhoneNumber(params),
  },
  {
    field: "whatsapp",
    headerName: "Whatsapp",
    width: 150,
    valueFormatter: (params) => formatPhoneNumber(params),
  },
  { field: "facebook", headerName: "Facebook", width: 200 },
  { field: "email", headerName: "Email", width: 200 },
  {
    field: "num_carte_fiscal",
    headerName: "NIF",
    width: 200,
    valueFormatter: (params) => (params === "null" ? "" : params),
  },
  {
    field: "numero_carte_stat",
    headerName: "STAT",
    width: 200,
    valueFormatter: (params) => (params === "null" ? "" : params),
  },
  { field: "libelle_type", headerName: "Poste postulé", width: 200 },
  { field: "Date_creation", headerName: "Postulé le", width: 200 },
  { field: "diplome", headerName: "Diplômes", width: 400 },
  { field: "experience", headerName: "Experiences", width: 400 },
  { field: "formation", headerName: "Formations", width: 400 },
  {
    field: "pretention_salariale",
    headerName: "Prétention salariale",
    width: 200,
    valueFormatter: (params) => (params ? showMoneyFormat(params, "MGA") : ""),
  },
  { field: "localisation", headerName: "Localisations", width: 300 },
  { field: "state", headerName: "Statut dossier", width: 150 },
  { field: "link", headerName: "Link", width: 400 },
];

const ListCandidat = ({ prec_url }) => {
  const { Toast, showToast } = useToast();
  const [idsButons, setIdsButons] = useState([]);
  const [rows, setRows] = useState([]);
  const [filtre, setFiltre] = useState({ mot: "", status: null });
  const [rowsData, setRowsData] = useState([]);
  const [loadingDonwDocs, setLoadingDonwDocs] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [colonne, setColonne] = useState([
    ...EXTERNAL_POSTULANT_ATTRIBUTES_COLUMNS,
    {
      field: "Actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <Grid>
          <LoadingButton
            loading={idsButons.includes(params.row.id_identite)}
            color="success"
            title={`Telecharger tous les documents envoyés par ${params.row.full_name}`}
            onClick={(e) => handleDownloadZip(params.row)}
          >
            <FileDownloadSharp />
          </LoadingButton>
        </Grid>
      ),
    },
  ]);
  const query = useQuery();
  const navigate = useNavigate();

  const handleDownloadExcel = () => {
    if (rows.length > 0) {
      exportToExcel(
        rows,
        EXTERNAL_POSTULANT_ATTRIBUTES_COLUMNS,
        rows.at(0).libelle_type || "Liste des candidats",
        false
      );
    } else {
      showToast(
        TYPE_NOTIFICATION.ERROR,
        "Erreur de l'exportation de données excel"
      );
    }
  };

  const handleDownloadAllFiles = (
    id_position,
    file_name = "Documents envoyés",
    state = "complet"
  ) => {
    setLoadingDonwDocs(true);
    if (query.get("id_type")) {
      handleLoadThisButton(id_position);
      historiqueService
        .downloadAllDocByIdPosition(id_position, filtre.status)
        .then((response) => {
          if (response.status !== 200) {
            showToast(TYPE_NOTIFICATION.ERROR, "Echec de telechargement");
            handleLoadThisButton(false);
            return;
          }
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `${file_name || "Document de tous les candidats"} .zip`
          );
          document.body.appendChild(link);
          link.click();
        })
        .catch((e) => {
          showToast(TYPE_NOTIFICATION.ERROR, e.tosTring());
        })
        .finally(() => {
          handleUnloadThisButton(id_position);
          setLoadingDonwDocs(false);
        });
    } else {
      showToast(TYPE_NOTIFICATION.ERROR, "Erreur de telechargement");
    }
  };

  const handleDownloadZip = (row) => {
    handleLoadThisButton(row.id_identite);
    showToast(TYPE_NOTIFICATION.INFO, "Chargement des données à compresser...");
    historiqueService
      .downloadUploadedFiles(row)
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Document de " + row.full_name + ".zip");
        document.body.appendChild(link);
        link.click();
      })
      .catch((error) => {
        showToast(TYPE_NOTIFICATION.ERROR, "Aucun fichier à télécharger");
      })
      .finally(() => {
        handleUnloadThisButton(row.id_identite);
      });
  };

  useEffect(() => {
    let token = query.get("ref");
    if (isTokenCreatedToday(token)) {
      localStorage.setItem("CODE_ADMIN", token);
    } else {
      showToast(TYPE_NOTIFICATION.ERROR, "Token non fourni ou invalide");
      navigate("/");
    }
    if (query.get("id_type")) {
      handleFetchCandidatList(query.get("id_type"));
    } else {
      showToast(TYPE_NOTIFICATION.ERROR, "Offre d'emploi non identifié");
    }
    let col_with_action = colonne;

    // if (!col_with_action.some(col => col.headerName === "Actions")) {
    //   col_with_action.push();
    // }
    setColonne(col_with_action);
  }, []);

  const handleFilter = (filtre) => {
    if (!filtre || (!filtre.mot && !filtre.status)) {
      setRowsData(rows);
      return;
    }

    const mots = filtre.mot
      .toLowerCase()
      .split(" ")
      .filter((mot) => mot.trim() !== "");

    setRowsData(
      rows.filter((item) =>
        mots.every((mot) =>
          Object.values(item).some(
            (val) => val && val.toString().toLowerCase().includes(mot)
          )
        )
      )
    );
  };

  const handleLoadThisButton = (ids) => {
    setIdsButons([...idsButons, ids]);
  };

  const handleUnloadThisButton = (ids) => {
    setIdsButons(idsButons.filter((id) => id !== ids));
  };

  const handleFetchCandidatList = (id_type, state = "complet") => {
    setLoadingData(true);
    historiqueService
      .getListeCandidat(id_type, state)
      .then((res) => {
        if (res && res.data) {
          setRows(res.data);
          setRowsData(res.data);
        }
      })
      .finally(() => {
        setLoadingData(false);
      });
  };

  useEffect(() => {
    if (filtre) {
      handleFilter(filtre);
    }
  }, [filtre]);

  useEffect(() => {
    handleFetchCandidatList(query.get("id_type"), filtre.status);
  }, [filtre.status]);

  return (
    <Grid container style={{ display: "flex", justifyContent: "center" }}>
      <Menu />
      <Grid
        item
        xs={12}
        md={10}
        lg={7}
        minHeight={"90vh"}
        borderRadius={"20px"}
        border={"2px rgba(11, 93, 208,0.5) solid "}
        boxShadow="0px 4px 10px rgba(7,71,161,0.4)"
        display={"flex"}
        flexDirection={"column"}
        p={2.5}
      >
        <Grid container justifyContent={"center"}>
          <img
            src={logo}
            className="App-logo"
            alt="logo"
            style={{
              maxWidth: "75px",
              maxHeight: "75px",
              // marginLeft: "40%",
              // display: window.innerWidth <= 600 ? "block" : "none",
            }}
          />
        </Grid>
        <Grid item>
          <Grid display={"flex"} flexDirection={"row"} alignContent={"center"}>
            <Grid item alignContent={"center"}>
              <Button
                color="primary"
                title="Retour à la liste des offres externes"
                onClick={(e) =>
                  navigate(`/administrations?ref=${query.get("ref")}`)
                }
                startIcon={<ArrowBack />}
              >
                <Typography
                  variant="body2"
                  display={{ xs: "none", md: "flex" }}
                >
                  Retour
                </Typography>
              </Button>
            </Grid>
            <Grid item textAlign={"center"} mr={8} flexGrow={1}>
              <h1>Liste des candidats</h1>
            </Grid>
          </Grid>
          <Toast />
          <Box height={"100%"}>
            <Grid
              item
              display={"flex"}
              flexDirection={"column"}
              alignItems={"center"}
              mb={1}
            >
              <Grid
                container
                spacing={1}
                display={"flex"}
                flexDirection={"row"}
                width={"100%"}
                mb={2}
              >
                <Grid
                  item
                  xs={6}
                  lg={4}
                  xl={3}
                  height={100}
                  display={"flex"}
                  alignContent={"center"}
                >
                  <Paper
                    sx={{
                      width: "100%",
                      boxShadow: "0px 2px 3px rgba(7,71,161,0.4)",
                      borderLeft: "2px solid rgb(47, 127, 240)",
                    }}
                  >
                    <Grid>
                      <CardHeader
                        title={
                          <Grid
                            container
                            display={"flex"}
                            flexDirection={"row"}
                            alignContent={"center"}
                            justifyContent={"space-between"}
                          >
                            <Grid alignContent={"center"}>
                              Effectif des postulants
                            </Grid>
                            <Grid>
                              {/* <IconButton>
                                <Sort />
                              </IconButton> */}
                            </Grid>
                          </Grid>
                        }
                        titleTypographyProps={{ variant: "body2" }}
                        subheader={rowsData.length}
                        subheaderTypographyProps={{
                          mt: 1,
                          color: "rgba(7,71,161,1)",
                          // textAlign: "center",
                          fontSize: "25px",
                        }}
                      />
                    </Grid>
                  </Paper>
                </Grid>
                <Grid
                  item
                  xs={6}
                  lg={4}
                  xl={3}
                  height={100}
                  display={"flex"}
                  alignContent={"center"}
                >
                  <Paper
                    sx={{
                      width: "100%",
                      boxShadow: "0px 2px 3px rgba(7,71,161,0.4)",
                      borderLeft: "2px solid rgb(47, 127, 240)",
                    }}
                  >
                    <Grid>
                      <CardHeader
                        title={
                          <Grid
                            container
                            display={"flex"}
                            flexDirection={"row"}
                            alignContent={"center"}
                            justifyContent={"space-between"}
                          >
                            <Grid alignContent={"center"}>
                              Niveau universitaire
                            </Grid>
                            <Grid>
                              {/* <IconButton>
                                <SchoolSharp />
                              </IconButton> */}
                            </Grid>
                          </Grid>
                        }
                        titleTypographyProps={{ variant: "body2" }}
                        subheader={
                          rowsData.filter((item) => {
                            const diplome = (item.diplome || "").toLowerCase();
                            return [
                              "licence",
                              "master",
                              "dea",
                              "aea",
                              "doctorat",
                              "maitrise",
                              "bacc+",
                            ].some((d) => diplome.includes(d.toLowerCase()));
                          }).length
                        }
                        subheaderTypographyProps={{
                          mt: 1,
                          color: "rgba(7,71,161,1)",
                          // textAlign: "center",
                          fontSize: "25px",
                        }}
                      />
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
              <Grid
                container
                spacing={1}
                textAlign={"center"}
                display={"flex"}
                justifyContent={"center"}
                flexDirection={"row"}
                width={"100%"}
              >
                <Grid item flexGrow={1}>
                  <TextField
                    id="recherche"
                    size="small"
                    fullWidth
                    onChange={(e) =>
                      setFiltre({ ...filtre, mot: e.target.value })
                    }
                    placeholder="Recherche"
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchOutlined />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Grid>
                <Grid item alignContent={"center"}>
                  <Autocomplete
                    size="small"
                    disablePortal
                    options={["complet", "incomplet"]}
                    onChange={(e, val) => setFiltre({ ...filtre, status: val })}
                    sx={{ width: 160 }}
                    renderInput={(params) => (
                      <TextField {...params} label="Status" />
                    )}
                  />
                </Grid>
                <Grid item alignContent={"center"}>
                  <Button
                    // variant="outlined"
                    title="Exporter la liste des candidats en excel"
                    onClick={(e) => handleDownloadExcel()}
                  >
                    <ImportExportIcon fontSize="medium" />
                    <Typography
                      variant="body2"
                      display={{ xs: "none", md: "flex" }}
                    >
                      Excel export
                    </Typography>
                  </Button>
                </Grid>
                <Grid item alignContent={"center"}>
                  <LoadingButton
                    loading={loadingDonwDocs}
                    // variant="outlined"
                    color="success"
                    title="Telecharger tous les documents envoyés"
                    onClick={(e) =>
                      handleDownloadAllFiles(
                        query.get("id_type"),
                        rows.at(0)?.libelle_type || "Tous les documents envoyés"
                      )
                    }
                  >
                    <FileDownloadSharp fontSize="medium" />
                    <Typography
                      variant="body2"
                      display={{ xs: "none", md: "flex" }}
                    >
                      Tous les documents
                    </Typography>
                  </LoadingButton>
                </Grid>
              </Grid>
            </Grid>
            <Grid2
              // p={{ xs: 1, lg: 5 }}
              maxWidth={"98vw"}
              height="60vh"
              flexGrow={1}
            >
              <StandardSkeletoon col={3} row={8} show={loadingData} />
              <DataGrid
                columns={colonne.filter(
                  (item) =>
                    !["pretention_salariale", "link", "id_identite"].includes(
                      item.field
                    )
                )}
                rows={rowsData}
                sx={{ height: "80%", display: loadingData ? "none" : "" }}
                getRowId={(row) => row.id_identite}
                localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
              />
            </Grid2>
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ListCandidat;
